import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { FileText, AlertCircle } from "lucide-react";
import { fetchCmsDocsByIdsApi } from "../../services/api/bookmarksApi";

const FONT_FAMILY =
  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const CmsSharedDocumentPage = () => {
  const { caseId, docId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [document, setDocument] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(false);
    fetchCmsDocsByIdsApi([{ docId, caseId }])
      .then((docs) => {
        if (cancelled) return;
        const doc = docs?.[0];
        if (!doc?.fileUrl) {
          setError(true);
          return;
        }
        setDocument(doc);
        window.open(doc.fileUrl, "_blank");
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, docId]);

  return (
    <>
      <Helmet>
        <title>LawGPT - Shared Document</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: "16px",
          padding: "24px",
          textAlign: "center",
        }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={32} sx={{ color: "#0073A6" }} />
            <Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: FONT_FAMILY }}>
              Opening shared document...
            </Typography>
          </>
        ) : error ? (
          <>
            <AlertCircle size={40} color="#EF4444" />
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#12243C", fontFamily: FONT_FAMILY }}>
              Document not found
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: FONT_FAMILY }}>
              This link may be invalid or the document may have been removed.
            </Typography>
          </>
        ) : (
          <>
            <FileText size={40} color="#0073A6" />
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#12243C", fontFamily: FONT_FAMILY }}>
              {document?.title || "Document ready"}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: FONT_FAMILY }}>
              If it didn't open automatically, use the button below.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.open(document.fileUrl, "_blank")}
              sx={{
                background: "linear-gradient(135deg, #0073A6 0%, #005A8B 100%)",
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                padding: "10px 24px",
              }}
            >
              Open document
            </Button>
          </>
        )}
      </Box>
    </>
  );
};

export default CmsSharedDocumentPage;
